// komponent wyszukiwarki oparty na MUI

import React, { FC, FormEvent, useEffect, useState, useCallback } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Search, X } from 'react-bootstrap-icons';

interface IWiseSearchProps {
  value?: string;
  placeholder?: string;
  onChange?: (phrase: string) => void;
  /** Wywoływane przy Enter lub kliknięciu ikonki lupy */
  onSubmit?: (phrase: string) => void;
  className?: string;
  debounced?: boolean;
  debounceTime?: number;
}

const WiseSearch: FC<IWiseSearchProps> = ({
  onChange,
  onSubmit,
  placeholder,
  value = '',
  className,
  debounced = false,
  debounceTime = 300
}) => {
  const [searchQuery, setSearchQuery] = useState(value);

  useEffect(() => {
    setSearchQuery(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    if (!debounced || typeof onChange !== 'function') return;
    const timer = window.setTimeout(() => {
      onChange(searchQuery);
    }, debounceTime);
    return () => window.clearTimeout(timer);
  }, [searchQuery, debounced, debounceTime, onChange]);

  const handleOnSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit?.(searchQuery);
    if (!debounced) {
      onChange?.(searchQuery);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit?.(searchQuery);
      if (!debounced) {
        onChange?.(searchQuery);
      }
    }
  };

  const handleClear = useCallback(() => {
    setSearchQuery('');
    onChange?.('');
  }, [onChange]);

  return (
    <form onSubmit={handleOnSubmit} className={className} style={{ width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        value={searchQuery}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton type="submit" aria-label="Search" edge="start" size="small">
                <Search />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClear}
                aria-label="Clear"
                edge="end"
                size="small"
                sx={{ padding: '4px' }}>
                <X size={20} />
              </IconButton>
            </InputAdornment>
          ) : null
        }}
      />
    </form>
  );
};

export default WiseSearch;
